import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import ReportsSubNav from "@/components/ReportsSubNav";
import ScopeToggle from "@/components/ScopeToggle";
import { usePipelineCtx } from "@/lib/pipelineContext.jsx";
import { MoreHorizontal, TrendingDown, TrendingUp } from "lucide-react";
import { SkeletonTile, SkeletonRows } from "@/components/Skeleton";

export default function PipelineDashboard() {
  const { pipelines, pipelineId } = usePipelineCtx();
  const [scope, setScope] = useState("pipeline"); // v5.7 Addition 22
  const [data, setData] = useState(null);

  useEffect(() => {
    if (scope === "pipeline" && !pipelineId) return;
    const params = scope === "tenant" ? { scope: "tenant" } : { pipeline_id: pipelineId };
    api.get("/reports/pipeline", { params }).then(({ data }) => setData(data));
  }, [pipelineId, scope]);

  const pipeline = pipelines.find((p) => p.pipeline_id === pipelineId);

  // In tenant mode, backend returns { scope, pipelines[], totals } — render an aggregate view.
  if (scope === "tenant" && data && data.scope === "tenant") {
    return (
      <div className="p-8">
        <div className="cw-caption uppercase tracking-widest">Reporting</div>
        <div className="mt-1 flex items-center justify-between gap-4">
          <h1 className="cw-h1" data-testid="report-pipeline-title">Pipeline — All Pipelines</h1>
          <ScopeToggle value={scope} onChange={setScope} />
        </div>
        <div className="mt-4"><ReportsSubNav /></div>
        <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
          <Tile label="Active Deals" value={data.totals.active_count} subLabel="Across all pipelines" testid="tile-active" />
          <Tile label="Accepted" value={data.totals.accepted} subLabel="All pipelines" testid="tile-accepted" />
          <Tile label="Rejected" value={data.totals.rejected} subLabel="All pipelines" testid="tile-rejected" />
          <Tile label="Stagnant" value={data.totals.stagnant_count} subLabel="All pipelines" testid="tile-stagnant" />
        </div>
        <div className="mt-6 cw-card overflow-hidden">
          <table className="w-full text-sm" data-testid="pipeline-breakdown-table">
            <thead className="bg-black/5">
              <tr>
                <th className="text-left px-4 py-2 font-medium">Pipeline</th>
                <th className="text-right px-4 py-2 font-medium">Active</th>
                <th className="text-right px-4 py-2 font-medium">Accepted</th>
                <th className="text-right px-4 py-2 font-medium">Rejected</th>
                <th className="text-right px-4 py-2 font-medium">Stagnant</th>
              </tr>
            </thead>
            <tbody>
              {data.pipelines.map((r) => (
                <tr key={r.pipeline_id} className="border-t" style={{ borderColor: "var(--cw-border)" }}>
                  <td className="px-4 py-2 font-medium">{r.pipeline_name}</td>
                  <td className="px-4 py-2 text-right">{r.active_count}</td>
                  <td className="px-4 py-2 text-right">{r.accepted}</td>
                  <td className="px-4 py-2 text-right">{r.rejected}</td>
                  <td className="px-4 py-2 text-right">{r.stagnant_count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="cw-caption uppercase tracking-widest">Reporting</div>
      <div className="mt-1 flex items-center justify-between gap-4">
        <div>
          <h1 className="cw-h1" data-testid="report-pipeline-title">
            Pipeline &mdash; {pipeline?.pipeline_name || "…"}
          </h1>
        </div>
        <div className="flex items-center gap-3">
          <ScopeToggle value={scope} onChange={setScope} />
          <button className="cw-btn-ghost p-2" data-testid="pipeline-overflow-menu" aria-label="More">
            <MoreHorizontal size={18} />
          </button>
        </div>
      </div>

      <div className="mt-4"><ReportsSubNav /></div>

      {!data ? (
        <div className="mt-6" data-testid="pipeline-dashboard-skeleton">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[0,1,2,3].map(i => <SkeletonTile key={i} />)}
          </div>
          <div className="mt-6 cw-card p-5">
            <SkeletonRows rows={6} />
          </div>
        </div>
      ) : (
        <>
          {/* KPI tiles */}
          <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
            <Tile
              label="Active Deals"
              value={data.active_count}
              subLabel="Currently in pipeline"
              subColor="var(--cw-muted)"
              testid="tile-active"
            />
            <Tile
              label="Avg Days Per Stage"
              value={`${data.avg_days_per_stage}d`}
              subLabel={
                data.avg_days_delta === null
                  ? "No 30-day baseline"
                  : `${data.avg_days_delta >= 0 ? "+" : ""}${data.avg_days_delta}d vs 30d ago`
              }
              subColor={
                data.avg_days_delta === null
                  ? "var(--cw-muted)"
                  : data.avg_days_delta < 0
                  ? "var(--cw-success)"
                  : "var(--cw-warning)"
              }
              subIcon={
                data.avg_days_delta === null ? null : data.avg_days_delta < 0
                  ? <TrendingDown size={11} />
                  : <TrendingUp size={11} />
              }
              testid="tile-avg-days"
            />
            <Tile
              label="Acceptance Rate"
              value={
                data.resolved === 0
                  ? "—"
                  : `${data.acceptance_rate}%`
              }
              subLabel={
                data.resolved === 0
                  ? "No resolved deals yet"
                  : `${data.accepted} accepted of ${data.resolved} resolved`
              }
              subColor={
                data.resolved === 0
                  ? "var(--cw-muted)"
                  : data.acceptance_rate >= 40
                  ? "var(--cw-success)"
                  : data.acceptance_rate >= 20
                  ? "var(--cw-warning)"
                  : "var(--cw-error)"
              }
              testid="tile-acceptance"
            />
            <Tile
              label="Stagnant Deals"
              value={data.stagnant_count}
              subLabel="Deals past stage threshold"
              subColor="var(--cw-error)"
              valueColor="var(--cw-error)"
              testid="tile-stagnant"
            />
          </div>

          {/* Funnel */}
          <Funnel funnel={data.funnel} />
        </>
      )}
    </div>
  );
}

function Tile({ label, value, subLabel, subColor, valueColor, subIcon, testid }) {
  return (
    <div className="cw-card p-4" data-testid={testid}>
      <div className="text-xs" style={{ color: "var(--cw-muted)" }}>{label}</div>
      <div className="text-[28px] font-bold mt-1"
        style={{ color: valueColor || "var(--cw-primary-dark)" }}
        data-testid={`${testid}-value`}>
        {value}
      </div>
      <div className="text-xs mt-1 inline-flex items-center gap-1" style={{ color: subColor }}>
        {subIcon} {subLabel}
      </div>
    </div>
  );
}

function Funnel({ funnel }) {
  const maxCount = Math.max(...funnel.map((f) => f.count), 1);
  const barFill = (row, idx) => {
    if (row.is_accepted_terminal) return "var(--cw-success)";
    if (row.is_rejected_terminal) return "var(--cw-error)";
    return idx % 2 === 0 ? "var(--cw-primary)" : "var(--cw-secondary)";
  };

  return (
    <div className="cw-card mt-6 p-5" data-testid="funnel-card">
      <h3 className="cw-h3">Pipeline Funnel</h3>
      <div className="mt-4 space-y-3">
        {funnel.map((row, idx) => {
          const widthPct = (row.count / maxCount) * 100;
          const isNarrow = widthPct < 15;
          return (
            <div key={row.stage_id} className="grid grid-cols-12 items-center gap-3"
              data-testid={`funnel-row-${row.stage_name}`}>
              <div className="col-span-3 text-sm text-[var(--cw-body)]">{row.stage_name}</div>
              <div className="col-span-8 relative bg-[#F5F5F5] rounded h-7 overflow-hidden">
                <div
                  className="h-full flex items-center px-2 text-white text-sm rounded"
                  style={{
                    width: `${Math.max(2, widthPct)}%`,
                    background: barFill(row, idx),
                    color: row.is_accepted_terminal || (idx % 2 === 0 && !row.is_rejected_terminal) ? "white" : "var(--cw-primary-dark)",
                    transition: "width 250ms ease",
                  }}
                >
                  {!isNarrow && row.count}
                </div>
                {isNarrow && (
                  <div
                    className="absolute top-0 h-full flex items-center text-sm"
                    style={{
                      left: `calc(${Math.max(2, widthPct)}% + 6px)`,
                      color: "var(--cw-body)",
                    }}
                  >
                    {row.count}
                  </div>
                )}
              </div>
              <div className="col-span-1 text-sm font-medium text-right">{row.count}</div>
            </div>
          );
        })}
      </div>
      <div className="cw-caption mt-4 leading-relaxed">
        Deal counts show all deals that ever entered each stage &mdash; a deal progressing through
        multiple stages is counted once per stage it reached. This is a cumulative funnel, not a
        snapshot of current active deals.
      </div>
    </div>
  );
}
